import { EntityKind, type EntityState, type PickUpRefusalReason } from "odyssey-shared";
import { readSetting, writeSetting, localStore, type SettingsStore } from "../settings.ts";
import { GROUND_REACH } from "./find.ts";

/**
 * Auto loot (P2.8-T79, ADR-091's amendment for P2.8-T79, point 4; the author, 2026-10-10: "we can then turn ON \"auto
 * loot\" (automatic pick up) or leave it off to select what to pick up"): a per-device choice, off by default, and,
 * while it is on, which of the player's own ground items the page asks the map server to pick up. An own item is one
 * `lootGained` marked for this page; the page asks for one as it comes within the pick-up's reach of the predicted
 * feet, the nearest first, through the same `pickUp` the interact key sends, so the map server checks every rule it
 * checks for a pick-up by hand. Requests go one at a time and at most one every `AUTO_LOOT_GAP_MS`, two a second, so
 * the item window (`MAX_ITEM_REQUESTS_PER_SECOND`, shared with using items, moving them and taking loot) keeps room for
 * the player's own requests. A final refusal (`full`, `owned`, `taken`, `dead`) ends the asking for that item while it
 * stays in view; a passing one (`reach`, `busy`, `unavailable`), or no answer within `AUTO_LOOT_ANSWER_MS`, as when
 * the window dropped the request, asks again after `AUTO_LOOT_RETRY_MS`.
 */

/**
 * The storage key the Auto loot choice lives under.
 */
export const AUTO_LOOT_KEY = "odyssey.autoLoot";

/**
 * The least time between two of Auto loot's requests, in milliseconds: two a second.
 */
export const AUTO_LOOT_GAP_MS = 500;

/**
 * How long a request waits for its item to leave the view or a refusal before it is given up, in milliseconds.
 */
export const AUTO_LOOT_ANSWER_MS = 1_000;

/**
 * How long an item a passing refusal answered, or whose request went unanswered, waits before it is asked for again,
 * in milliseconds.
 */
export const AUTO_LOOT_RETRY_MS = 1_000;

/**
 * The refusals after which Auto loot does not ask for the item again while it stays in view.
 */
const FINAL_REFUSALS: ReadonlySet<PickUpRefusalReason> = new Set<PickUpRefusalReason>(["full", "owned", "taken", "dead"]);

/**
 * Reads a stored value as the Auto loot choice, off by default.
 *
 * @param raw The stored text, or null when nothing is stored.
 * @returns True for exactly `on`.
 */
export function parseAutoLoot(raw: string | null): boolean {
  return raw === "on";
}

/**
 * Reads the viewer's Auto loot choice.
 *
 * @param store Where it is kept; this browser's `localStorage` by default.
 * @returns Whether Auto loot is on.
 */
export function readAutoLoot(store: SettingsStore | null = localStore()): boolean {
  return readSetting(AUTO_LOOT_KEY, parseAutoLoot, store);
}

/**
 * The page's Auto loot choice: made from what the store holds as the page loads, taking each change Settings makes,
 * which the next snapshot follows and which is kept per device.
 *
 * @example
 * const autoLoot = new AutoLootSetting(readAutoLoot());
 * autoLoot.setOn(true);
 */
export class AutoLootSetting {
  /**
   * @param value Whether Auto loot is on as the page loads.
   * @param store Where a change is kept; this browser's `localStorage` when omitted.
   */
  public constructor(
    private value: boolean,
    private readonly store?: SettingsStore | null,
  ) {}

  /**
   * Whether Auto loot is on now.
   *
   * @returns The choice.
   */
  public get on(): boolean {
    return this.value;
  }

  /**
   * Takes the viewer's choice and keeps it.
   *
   * @param on Whether Auto loot is on.
   */
  public setOn(on: boolean): void {
    this.value = on;
    writeSetting(AUTO_LOOT_KEY, on ? "on" : "off", this.store === undefined ? localStore() : this.store);
  }
}

/**
 * Which own item the page asks to pick up next.
 */
export class AutoLoot {
  private readonly done = new Set<number>();
  private readonly retryAt = new Map<number, number>();
  private awaiting = -1;
  private askedAt = 0;
  private lastAsk = Number.NEGATIVE_INFINITY;

  /**
   * @param on Whether Auto loot is on now, read at each snapshot.
   * @param now The page's clock in milliseconds.
   */
  public constructor(
    private readonly on: () => boolean,
    private readonly now: () => number = () => performance.now(),
  ) {}

  /**
   * The handle to ask for after an applied snapshot, if any: the nearest own item in reach that no final refusal
   * answered and whose pause is over, while no request waits and the gap since the last has passed. Forgets the items
   * that left the view, and gives up a request whose item left or that waited past `AUTO_LOOT_ANSWER_MS`.
   *
   * @param entities The client's view.
   * @param own Whether a handle is one of the page's own items.
   * @param feet The predicted feet in sixteenths, undefined while none is drawn.
   * @returns The handle, or -1 for none.
   */
  public next(entities: ReadonlyMap<number, EntityState>, own: (handle: number) => boolean, feet: { readonly x: number; readonly y: number } | undefined): number {
    const now = this.now();
    for (const handle of this.done) {
      if (entities.get(handle)?.kind !== EntityKind.GroundItem) {
        this.done.delete(handle);
      }
    }
    for (const handle of this.retryAt.keys()) {
      if (entities.get(handle)?.kind !== EntityKind.GroundItem) {
        this.retryAt.delete(handle);
      }
    }
    if (this.awaiting >= 0) {
      if (entities.get(this.awaiting)?.kind === EntityKind.GroundItem) {
        if (now - this.askedAt < AUTO_LOOT_ANSWER_MS) {
          return -1;
        }
        this.retryAt.set(this.awaiting, now + AUTO_LOOT_RETRY_MS);
      }
      this.awaiting = -1;
    }
    if (!this.on() || feet === undefined || now - this.lastAsk < AUTO_LOOT_GAP_MS) {
      return -1;
    }
    let best = -1;
    let bestDistance = Number.POSITIVE_INFINITY;
    for (const state of entities.values()) {
      if (state.kind !== EntityKind.GroundItem || this.done.has(state.handle) || (this.retryAt.get(state.handle) ?? now) > now || !own(state.handle)) {
        continue;
      }
      const dx = state.x - feet.x;
      const dy = state.y - feet.y;
      if (Math.abs(dx) > GROUND_REACH || Math.abs(dy) > GROUND_REACH) {
        continue;
      }
      const distance = dx * dx + dy * dy;
      if (distance < bestDistance) {
        best = state.handle;
        bestDistance = distance;
      }
    }
    if (best >= 0) {
      this.retryAt.delete(best);
      this.awaiting = best;
      this.askedAt = now;
      this.lastAsk = now;
    }
    return best;
  }

  /**
   * Whether a refusal answers the request Auto loot is waiting on, which the page then keeps quiet; that request is
   * ended, its item not asked for again while in view after a final refusal, and again after `AUTO_LOOT_RETRY_MS`
   * after a passing one.
   *
   * @param handle The refused item's handle.
   * @param reason The server's reason.
   * @returns True when the refusal was Auto loot's.
   */
  public refused(handle: number, reason: PickUpRefusalReason): boolean {
    if (handle < 0 || handle !== this.awaiting) {
      return false;
    }
    this.awaiting = -1;
    if (FINAL_REFUSALS.has(reason)) {
      this.done.add(handle);
    } else {
      this.retryAt.set(handle, this.now() + AUTO_LOOT_RETRY_MS);
    }
    return true;
  }

  /**
   * Forgets every request, as a session ends.
   */
  public clear(): void {
    this.done.clear();
    this.retryAt.clear();
    this.awaiting = -1;
    this.lastAsk = Number.NEGATIVE_INFINITY;
  }
}

/**
 * The page's one Auto loot choice, read from this browser's store as the page loads.
 */
export const pageAutoLoot = new AutoLootSetting(readAutoLoot());
